import React from 'react';
import {
  Form, FormHandlers, Grid, ICON_SQUARE_METERS, InputSize,
} from '@doveit/bricks';
import { buildTemplateFieldsInitialValues, buildTemplateFieldsValidationSchema, parseNumber } from './utils';
import { TemplateFieldConfig, TemplateFieldsFormValues } from '../../types';
import { noop } from '../../../utils';

export interface TemplateFieldsFormProps {
  innerRef?: React.MutableRefObject<FormHandlers>,
  fields: TemplateFieldConfig[],
  initialValues?: TemplateFieldsFormValues,
  disabled?: boolean,
  size?: InputSize,
  onFieldChange?: (fieldName: string, value: unknown) => void,
  onSubmit?: (values: TemplateFieldsFormValues) => void,
}

const TemplateFieldsForm: React.FC<TemplateFieldsFormProps> = ({
  innerRef,
  fields = [],
  initialValues = {},
  disabled,
  onFieldChange,
  onSubmit = noop,
}) => {
  const computedValidationSchema = React.useMemo(
    () => ((fields.length > 0) ? buildTemplateFieldsValidationSchema(fields) : undefined),
    [fields],
  );

  const computedInitialValues = React.useMemo(
    () => buildTemplateFieldsInitialValues(fields, initialValues),
    [fields, initialValues],
  );

  if (fields.length === 0) return null;

  return (
    <Form
      innerRef={innerRef}
      validationSchema={computedValidationSchema}
      initialValues={computedInitialValues}
      enableReinitialize
      disabled={disabled}
      onSubmit={onSubmit}
    >
      {({ values }) => fields.map((config) => {
        const inputProps = config.inputProps || {};

        switch (config.inputType) {
          case 'date-picker':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.Input
                  name={config.name}
                  type="date"
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    onFieldChange?.(config.fieldName, event.target.value ? new Date(event.target.value).toISOString() : undefined);
                  }}
                />
              </Form.Item>
            );

          case 'number-input':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.Input
                  name={config.name}
                  type="number"
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    onFieldChange?.(config.fieldName, parseNumber(event.target.value));
                  }}
                />
              </Form.Item>
            );

          case 'currency-input':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.DecimalPriceField
                  name={config.name}
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    onFieldChange?.(config.fieldName, parseNumber(event.target.value));
                  }}
                />
              </Form.Item>
            );

          case 'size-input':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.Input
                  name={config.name}
                  type="number"
                  min="0"
                  icon={ICON_SQUARE_METERS}
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    onFieldChange?.(config.fieldName, event.target.value);
                  }}
                />
              </Form.Item>
            );

          case 'select':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.Select
                  name={config.name}
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    onFieldChange?.(config.fieldName, event.target.value);
                  }}
                />
              </Form.Item>
            );

          case 'timerange-picker':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Grid gutter={50} addCSS={{ alignItems: 'flex-end' }}>
                  <Grid.Unit size={{ MD: 1 / 2 }}>
                    <Form.Input
                      name={`${config.name}Start`}
                      type="time"
                      label={config.label}
                      aria-label={config.label}
                      {...inputProps}
                      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                        onFieldChange?.(config.fieldName, `${event.target.value || 'XX:XX'} - ${(values[`${config.name}End`] as string) || 'XX:XX'}`);
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={{ MD: 1 / 2 }}>
                    <Form.Input
                      name={`${config.name}End`}
                      type="time"
                      aria-label={config.label}
                      {...inputProps}
                      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                        onFieldChange?.(config.fieldName, `${(values[`${config.name}Start`] as string) || 'XX:XX'} - ${event.target.value || 'XX:XX'}`);
                      }}
                    />
                  </Grid.Unit>
                </Grid>
              </Form.Item>
            );

          case 'time-unit-input':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Grid gutter={50} addCSS={{ alignItems: 'flex-end' }}>
                  <Grid.Unit size={{ MD: 1 / 3 }}>
                    <Form.Input
                      name={config.name}
                      type="number"
                      label={config.label}
                      aria-label={config.label}
                      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                        onFieldChange?.(`${config.fieldName}.time`, parseNumber(event.target.value));
                      }}
                    />
                  </Grid.Unit>
                  <Grid.Unit size={{ MD: 2 / 3 }}>
                    <Form.Select
                      name={`${config.name}Unit`}
                      aria-label={config.label}
                      {...inputProps}
                      onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
                        onFieldChange?.(`${config.fieldName}.unit`, event.target.value);
                      }}
                    />
                  </Grid.Unit>
                </Grid>
              </Form.Item>
            );

          case 'text-area':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.TextArea
                  name={config.name}
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    onFieldChange?.(config.fieldName, event.target.value);
                  }}
                />
              </Form.Item>
            );
          case 'autocomplete':
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.AutoCompleteV2
                  name={config.name}
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onChange={(event: React.FocusEvent<HTMLInputElement>) => {
                    onFieldChange?.(config.fieldName, event.target.value);
                  }}
                />
              </Form.Item>
            );
          case 'text-input':
          default:
            return (
              <Form.Item key={config.name} size={{ MD: config.gridUnit }}>
                <Form.Input
                  name={config.name}
                  type="text"
                  label={config.label}
                  aria-label={config.label}
                  {...inputProps}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    onFieldChange?.(config.fieldName, event.target.value);
                  }}
                />
              </Form.Item>
            );
        }
      })}
    </Form>
  );
};

export default TemplateFieldsForm;
